import { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { ClipboardList, Search, ChevronLeft, ChevronRight, Plus, Pencil, Copy, Tag as TagIcon, Columns, RotateCcw, PenLine, History, Trash2, ListPlus, Maximize2, Minimize2, SlidersHorizontal, ListFilter } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { NewProcurementQuoteDialog } from "@/components/NewProcurementQuoteDialog";
import { ProductThumb } from "@/components/products/ProductThumb";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import { useColumnLayout } from "@/hooks/useColumnLayout";
import { PqMobileList } from "@/components/procurement/PqMobileList";
import { PqMobileFilterSheet } from "@/components/procurement/PqMobileFilterSheet";
import { PqSyncStatus } from "@/components/procurement/PqSyncStatus";
import { TemplatePickerDialog } from "@/components/procurement/TemplatePickerDialog";
import { FloatingHScrollbar } from "@/components/FloatingHScrollbar";

const PAGE_SIZE = 50;

// Numeric fields — send as Number(); everything else stays string.
const NUMERIC_FIELDS = new Set(["price", "price_per_meter", "qty_hq", "width", "length", "height", "gross_weight"]);

// 2026-02-27 · Fields feeding the backend price formula. When any of them
// changes we do an optimistic ``price`` recompute on the client so the UI
// doesn't lag the PUT round-trip. Backend response wins on mismatch.
// Exported so ``ProcurementHistoryDialog`` can share the same set.
export const PRICE_FORMULA_INPUTS_EXPORT = new Set([
  "price_per_meter", "material_size", "processing_fee", "brand_paper",
  "plastic_bag", "liner_board", "carton_freight", "others", "profit_tax",
]);
const PRICE_FORMULA_INPUTS = PRICE_FORMULA_INPUTS_EXPORT;
// 2026-06 · Formula lives in lib/pqPrice.js (shared with the edit dialog).
import { computePqPrice } from "@/lib/pqPrice";
import { OtherCostDetailsDialog, sumOtherDetails } from "@/components/OtherCostDetailsDialog";
export { computePqPrice };

// Fields the inline editor is allowed to modify (matches the edit dialog).
// item_number stays read-only inline because it's the natural row key.
const INLINE_EDITABLE = new Set([
  "quote_customer", "quote_date", "name_cn", "size", "thickness", "factory", "price", "price_per_meter",
  "material_size", "processing_fee", "brand_paper", "plastic_bag", "liner_board",
  "carton_freight", "profit_tax", "others", "packing_rate", "length", "width", "height",
  "qty_hq", "gross_weight", "salesperson", "remark",
]);

// snake_case → camelCase for translation keys (item_number → itemNumber).
const camelKey = (k) => k.replace(/_([a-z])/g, (_, c) => c.toUpperCase());

export const COLUMNS = [
  // 2026-02 · 报价客户 placed BEFORE the date per user spec — most quote
  // sheets in the wild lead with "who's this quote for?", then when.
  // 2026-02-27 · Product photo is its OWN column, sitting just before 报价客户
  // per user spec (same column width as 报价客户 for visual balance).
  { key: "product_photo", label: "pq.productPhoto", w: "w-28" },
  { key: "quote_customer", label: "pq.quoteCustomer", w: "w-28" },
  { key: "quote_date", label: "pq.date", w: "w-24" },
  { key: "item_number", label: "pq.itemNumber", mono: true },
  { key: "name_cn", label: "pq.nameCn" },
  { key: "size", label: "pq.size" },
  { key: "thickness", label: "pq.thickness" },
  { key: "factory", label: "pq.factory" },
  { key: "price", label: "pq.price", num: true },
  { key: "price_per_meter", label: "pq.pricePerMeter", num: true },
  { key: "material_size", label: "pq.materialSize" },
  { key: "processing_fee", label: "pq.processingFee" },
  { key: "brand_paper", label: "pq.brandPaper" },
  { key: "plastic_bag", label: "pq.plasticBag" },
  { key: "liner_board", label: "pq.linerBoard" },
  { key: "carton_freight", label: "pq.cartonFreight" },
  { key: "profit_tax", label: "pq.profitTax" },
  { key: "others", label: "pq.others" },
  // 2026-06 · 其他1/2/3 合并成一列显示（FM 只读，已计入价格公式）
  { key: "others_123", label: "pq.others123" },
  { key: "packing_rate", label: "pq.packingRate" },
  { key: "length", label: "pq.length", num: true },
  { key: "width", label: "pq.width", num: true },
  { key: "height", label: "pq.height", num: true },
  { key: "qty_hq", label: "pq.qtyHq", num: true },
  { key: "gross_weight", label: "pq.grossWeight", num: true },
  { key: "salesperson", label: "pq.salesperson" },
  { key: "remark", label: "pq.remark", wide: true },
];

export function cellValue(row, col) {
  // 2026-06 · 其他1/2/3 合并展示为 "5 / 3 / 2"（空值显示 -）
  if (col.key === "others_123") {
    const parts = [row.others1, row.others2, row.others3].map((v) => (v === null || v === undefined || v === "" ? "-" : String(v)));
    return parts.every((p) => p === "-") ? "-" : parts.join(" / ");
  }
  const v = row[col.key];
  if (col.num) return v ? Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 }) : "-";
  return v || "-";
}

// Default column widths (px) — used when localStorage has no override.
// 2026-02 v6 per user spec: 中文名称 500 (was 200, ×2.5), 尺寸 65 (was 130, ÷2).
// Row height auto-grows via ``whitespace-normal break-words`` on cells (already applied).
const DEFAULT_COL_WIDTHS = {
  actions: 120, tag: 90,
  product_photo: 130,
  quote_customer: 130,
  quote_date: 96, item_number: 110, name_cn: 500, size: 65, thickness: 110,
  factory: 110, price: 78, price_per_meter: 90, material_size: 90,
  processing_fee: 78, brand_paper: 78, plastic_bag: 78, liner_board: 74,
  carton_freight: 90, profit_tax: 78, others: 74, others_123: 104, packing_rate: 90,
  length: 66, width: 66, height: 66, qty_hq: 84, gross_weight: 80, salesperson: 90, remark: 240,
};

/** 2026-06 · 采购报价全屏 — portal to <body> so the app header/sidebar don't sit on top. */
const PqFullscreenBox = ({ active, children }) => {
  if (!active) return <div className="animate-fade-in-up" data-testid="pq-page-wrapper">{children}</div>;
  return createPortal(
    <div data-testid="pq-page-wrapper"
      className="fixed inset-0 z-40 bg-background p-2 overflow-hidden flex flex-col">
      {children}
    </div>,
    document.body,
  );
};

export default function ProcurementQuotes() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const canCreate = user && (user.role === "admin" || user.role === "buyer" || user.role === "procurement_manager");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  // 2026-02-09 · Date filter restored (blank by default so users see the
  // full history on load; they pick the range manually when they need it).
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [buyer, setBuyer] = useState("all");
  const [buyers, setBuyers] = useState([]);
  // 2026-02 · 报价客户 filter — behaves like the buyer/salesperson dropdown.
  const [customer, setCustomer] = useState("all");
  const [customers, setCustomers] = useState([]);
  // 2026-02-09 · 采购价格区间 (price min/max) — stored as strings so blank
  // inputs stay blank instead of being coerced to 0.
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [loading, setLoading] = useState(true);
  // 2026-02-09 · Row-highlight state — the row whose cells the cursor is
  // currently over lights up bright yellow. (Earlier iteration highlighted
  // whole columns; user reverted to row highlight per spec.)
  const [hoverRowId, setHoverRowId] = useState(null);
  // 2026-06 · Row just created by 复制 — highlighted + scrolled into view.
  const [highlightId, setHighlightId] = useState(null);
  // ISO date used only to seed the ``quote_date`` field of newly-created rows.
  const todayIso = new Date().toISOString().slice(0, 10);
  // 2026-02-09 · Salesperson filter defaults to "全部采购" (all) per user
  // spec — previous auto-default to the logged-in user's name was removed.
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  // 2026-09-30 · "从模版新建" — 挑完模版后进入新建对话框，字段按模版默认 +
  // 产品种类默认值预填，并用模版公式实时算价。
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [activeCategoryDefaults, setActiveCategoryDefaults] = useState(null);
  const [colDialogOpen, setColDialogOpen] = useState(false);  // 2026-02 v3 · dedicated "Adjust column widths" dialog
  // 2026-02-26 · Per-item-number modification history modal.
  const [historyItem, setHistoryItem] = useState(null);  // item_number string
  const [otherDetailsRow, setOtherDetailsRow] = useState(null);  // 其他明细 target row
  // 2026-06 · 备注小窗 —— 点列表里的备注单元格打开，方便整段阅读 / 粘贴长文本。
  const [noteRow, setNoteRow] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  // 2026-02-26 · Inline edit mode (toolbar toggle). When ON, cells the user is
  // allowed to edit render as <input> boxes and auto-save onBlur.
  const [editMode, setEditMode] = useState(false);
  // 2026-06 · 全屏模式 (user spec) — 顶部筛选条 + 表格 + 分页都保留。
  const [fullscreen, setFullscreen] = useState(false);
  // 2026-06 · 手机端 (采购主用)：筛选抽屉 / 卡片字段设置 / 加载更多累加的行
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [fieldSheetOpen, setFieldSheetOpen] = useState(false);
  const [appendedRows, setAppendedRows] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);
  // Track which (rowId, field) is currently mid-save so we can show a subtle
  // saving indicator + block duplicate PUTs while one is in flight.
  const [savingCell, setSavingCell] = useState(null);
  // Uncontrolled draft strings for the width inputs so mid-type re-renders never
  // reset the cursor. Committed to ``colWidths`` on blur / Enter (v4 fix).
  const [widthDrafts, setWidthDrafts] = useState({});
  // Column widths (2026-02, refactored 2026-06 to the shared hook —
  // see hooks/useColumnLayout.js). Width 0 = hidden column (user spec).
  const { widths: colWidths, setWidths: setColWidths, startResize: startColResize, persist: persistWidths } =
    useColumnLayout("quotesys_pq_widths_v2", DEFAULT_COL_WIDTHS);
  // 2026-06 · Floating horizontal scrollbar pinned to the viewport bottom so
  // panning wide tables never requires scrolling to the end of the page.
  const tableScrollRef = useRef(null);
  // 2026-06 · 表格自身高度收到视口底部 → 垂直滚动发生在表格内部，表头 (sticky
  // top-0) 与冻结的「操作 / 产品编号」列 (sticky left) 才能一直留在视野里。
  const [boxMaxH, setBoxMaxH] = useState(null);

  const widthsRef = useRef(colWidths);
  widthsRef.current = colWidths;
  const startResize = (e, key) => startColResize(key, 48)(e);

  const commitWidths = useCallback((next) => {
    setColWidths(next);
    persistWidths(next);
  }, [setColWidths, persistWidths]);

  // Reset one or all columns back to the DEFAULT_COL_WIDTHS baseline.
  const resetColWidth = (key) => {
    commitWidths({ ...widthsRef.current, [key]: DEFAULT_COL_WIDTHS[key] || 100 });
  };
  const resetAllColWidths = () => {
    commitWidths({ ...DEFAULT_COL_WIDTHS });
  };

  // Double-click handler: quick auto-fit to a reasonable width for the column type
  // (numeric columns squish tighter; text columns get more room). Users can then
  // fine-tune in the dialog if they want.
  const autoFitColumn = (key) => {
    const col = COLUMNS.find((c) => c.key === key);
    let target = DEFAULT_COL_WIDTHS[key] || 100;
    if (col?.num || col?.mono) target = Math.max(76, target);
    else if (key === "remark" || key === "name_cn") target = 280;
    else if (key === "size" || key === "thickness" || key === "factory") target = 140;
    commitWidths({ ...widthsRef.current, [key]: target });
  };

  // Total table width in px — sum of all col widths. Guarantees every column
  // renders at exactly its assigned width (2026-02 v7 fix: previously used
  // ``width: max-content`` which let long unbreakable cell content stretch
  // individual columns beyond their col style).
  // Setting a width of 0 = HIDE the column (2026-02 v8 · per user spec
  // 「如果调整列宽为 0 的话，就隐藏这个字段」).
  const isHidden = (key) => (Number(colWidths[key]) || 0) === 0;
  const totalWidth = Object.entries(colWidths).reduce(
    (s, [, w]) => s + (Number(w) || 0), 0
  );
  // 冻结列的左偏移：操作列钉在最左，产品编号紧随其后（用户选择 b）。
  const frozenActionsW = isHidden("actions") ? 0 : (Number(colWidths.actions) || 0);

  // Constrain the table box to the viewport so its own vertical scrollbar (and
  // therefore the sticky header) takes over from the page scroll.
  useEffect(() => {
    if (fullscreen) { setBoxMaxH(null); return; }
    const calc = () => {
      const el = tableScrollRef.current;
      if (!el || getComputedStyle(el).display === "none") return;
      const docTop = el.getBoundingClientRect().top + window.scrollY;
      setBoxMaxH(Math.max(320, Math.round(window.innerHeight - docTop - 64)));
    };
    calc();
    const tm = setTimeout(calc, 400);   // after filters/toolbar settle
    window.addEventListener("resize", calc);
    return () => { clearTimeout(tm); window.removeEventListener("resize", calc); };
  }, [fullscreen]);


  // Flat list of all resizable columns for the dialog (actions + tag + COLUMNS).
  const ALL_COLS = [
    { key: "actions", label: t("common.actions") },
    { key: "tag", label: t("pq.tag") },
    ...COLUMNS.map((c) => ({ key: c.key, label: t(c.label) })),
  ];

  // Buyer edit rule (2026-02-27 · FM rows also editable per user spec):
  //   admin: any row.
  //   buyer: any row where salesperson == own name (any source).
  //   procurement_manager: any row where salesperson ∈ subordinate list.
  const subordinateNamesLower = (user?.subordinate_names_list || []).map((n) => (n || "").trim().toLowerCase());
  const canEditRow = (r) => {
    if (user?.role === "admin") return true;
    if (user?.role === "procurement_manager") {
      const sp = (r.salesperson || "").trim().toLowerCase();
      return Boolean(sp) && subordinateNamesLower.includes(sp);
    }
    if (user?.role !== "buyer") return false;
    return (r.salesperson || "").trim().toLowerCase() === (user?.name || "").trim().toLowerCase();
  };
  // Duplicate: anyone with create permission (admin/buyer) can duplicate any quote.
  const canDuplicate = () => canCreate;

  const handleDuplicate = async (r) => {
    try {
      const { data } = await api.post(`/procurement-quotes/${r.id}/duplicate`);
      toast.success(t("pq.duplicatedOk"));
      // 2026-06 · 复制后经常「找不到新记录」：清掉可能把它挡在视图外的筛选、
      // 回到第 1 页，高亮定位新行，并直接打开编辑弹窗让用户立即修改。
      setSearch(""); setQuery("");
      setDateFrom(""); setDateTo("");
      setBuyer("all"); setCustomer("all");
      setPriceMin(""); setPriceMax("");
      setAppendedRows([]);
      setPage(1);
      if (data?.id) {
        setHighlightId(data.id);
        setEditingRow(data);
      }
      fetchRows();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("common.error"));
    }
  };

  const handleTag = async (r) => {
    const current = r.tag || "";
    // eslint-disable-next-line no-alert
    const next = window.prompt(t("pq.tagPrompt"), current);
    if (next === null) return;   // cancelled
    try {
      await api.patch(`/procurement-quotes/${r.id}/tag`, { tag: next.trim() });
      toast.success(t("pq.tagSaved"));
      fetchRows();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("common.error"));
    }
  };

  // 2026-02 · Delete own same-day quote (user spec). Backend enforces the
  // 2026-02-27 · Delete permission now matches edit rules (per user spec —
  // FM rows deletable too, buyer no longer bound to same-day). Backend
  // enqueues a tombstone so the next sync click also deletes on FM.
  const isSameDayOwn = (r) => {
    if (user?.role === "admin") return true;
    if (user?.role === "procurement_manager") {
      const sp = (r.salesperson || "").trim().toLowerCase();
      return Boolean(sp) && subordinateNamesLower.includes(sp);
    }
    if (user?.role !== "buyer") return false;
    const me = (user?.name || "").trim().toLowerCase();
    const owner = (r.salesperson || "").trim().toLowerCase();
    return Boolean(me) && me === owner;
  };
  const handleDelete = async (r) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t("pq.deleteConfirm"))) return;
    try {
      await api.delete(`/procurement-quotes/${r.id}`);
      toast.success(t("pq.deleted"));
      fetchRows();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("common.error"));
    }
  };

  // Partial field patch: unrelated stale cells never travel with an edit.
  const buildPqPayload = (row, field, value) => ({
    [field]: value,
    base_values: { [field]: row[field] ?? null },
  });

  // 2026-02-27 · Pending-saves registry — the count of in-flight PUTs. Used
  // to gate the ``beforeunload`` warning + the "flush before close" wait so
  // users can't lose data by closing the tab mid-typing.
  const pendingSavesRef = useRef(new Set());

  const saveRowField = async (row, field, rawValue) => {
    // Normalise for comparison to skip no-op saves.
    const isNum = NUMERIC_FIELDS.has(field);
    const nextValue = isNum ? (rawValue === "" ? 0 : Number(rawValue)) : rawValue;
    if (isNum && !Number.isFinite(nextValue)) {
      toast.error(t("pq.invalidNumber"));
      return;
    }
    const prevValue = isNum ? Number(row[field] ?? 0) : (row[field] ?? "");
    if (String(prevValue) === String(nextValue)) return;  // no-op

    // Required fields must not be blanked out inline (backend would 422).
    if (["item_number", "name_cn", "size", "factory"].includes(field)
        && String(nextValue).trim() === "") {
      toast.error(`${t(`pq.${camelKey(field)}`)} — ${t("pq.required")}`);
      return;
    }

    // Optimistic update — reflect the edit (and the recomputed price when
    // applicable) immediately so the UI never lags the network. If the
    // backend disagrees the response spread below overrides.
    const optimisticRow = { ...row, [field]: nextValue };
    if (PRICE_FORMULA_INPUTS.has(field)) {
      optimisticRow.price = computePqPrice(optimisticRow);
    }
    setRows((prev) => prev.map((r) => (r.id === row.id ? optimisticRow : r)));

    const cellKey = `${row.id}:${field}`;
    setSavingCell(cellKey);
    pendingSavesRef.current.add(cellKey);
    try {
      const payload = buildPqPayload(row, field, nextValue);
      const { data } = await api.put(`/procurement-quotes/${row.id}`, payload);
      setRows((prev) => prev.map((r) => (r.id === row.id ? {
        ...r, [field]: data[field], price: data.price, qty_hq: data.qty_hq,
        sync_status: data.sync_status, sync_error: data.sync_error,
      } : r)));
      toast.success(t("pq.savedApp"));
    } catch (e) {
      // Roll back the optimistic change on failure so the row's data
      // matches server state instead of the failed keystroke.
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, [field]: row[field] } : r)));
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("pq.autoSaveFailed"));
    } finally {
      pendingSavesRef.current.delete(cellKey);
      setSavingCell((cur) => (cur === cellKey ? null : cur));
    }
  };

  const saveOtherDetails = async (row, details) => {
    const total = sumOtherDetails(details);
    const nextRow = { ...row, others_details: details, others: details.length ? String(total) : row.others };
    nextRow.price = computePqPrice(nextRow);
    setRows((prev) => prev.map((r) => (r.id === row.id ? nextRow : r)));
    try {
      const payload = { others_details: details, base_values: { others_details: row.others_details ?? null } };
      const { data } = await api.put(`/procurement-quotes/${row.id}`, payload);
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, ...data } : r)));
      toast.success(t("pq.autoSaved"));
    } catch (e) {
      setRows((prev) => prev.map((r) => (r.id === row.id ? row : r)));
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("pq.autoSaveFailed"));
    }
  };

  // 2026-02-27 · Guard against data-loss on tab close / navigate-away when
  // an autosave PUT is still in flight (fixes user-reported "closing the
  // list drops some edits" bug). Also flushes any input still focused so
  // the blur-triggered save actually fires before we unmount.
  useEffect(() => {
    const flushFocused = () => {
      const active = document.activeElement;
      if (active && active.tagName === "INPUT" && active.dataset && active.dataset.testid
          && String(active.dataset.testid).startsWith("pq-inline-")) {
        active.blur();
      }
    };
    const handler = (e) => {
      flushFocused();
      if (pendingSavesRef.current.size > 0) {
        // Modern browsers ignore custom text but still show a generic prompt
        // — that's enough to let the in-flight PUT complete.
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
      return undefined;
    };
    window.addEventListener("beforeunload", handler);
    return () => {
      flushFocused();
      window.removeEventListener("beforeunload", handler);
    };
  }, []);

  const toggleEditMode = () => {
    setEditMode((m) => {
      const next = !m;
      toast[next ? "success" : "message"](next ? t("pq.editModeOn") : t("pq.editModeOff"));
      return next;
    });
  };

  // debounce search input
  useEffect(() => {
    const id = setTimeout(() => {
      setQuery(search.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(id);
  }, [search]);

  const fetchSeqRef = useRef(0);
  const fetchRows = useCallback(async () => {
    setLoading(true);
    const mySeq = ++fetchSeqRef.current;
    try {
      // 2026-02-09 · Filters (日期 / 采购员 / 报价客户 / 价格区间) now
      // always apply, even during search — so users can refine the search
      // result set instead of having to clear the query first.
      const { data } = await api.get("/procurement-quotes", {
        params: {
          page,
          page_size: PAGE_SIZE,
          ...(query ? { search: query } : {}),
          ...(dateFrom ? { date_from: dateFrom } : {}),
          ...(dateTo ? { date_to: dateTo } : {}),
          ...(buyer !== "all" ? { salesperson: buyer } : {}),
          ...(customer !== "all" ? { quote_customer: customer } : {}),
          ...(priceMin !== "" && !Number.isNaN(Number(priceMin)) ? { price_min: Number(priceMin) } : {}),
          ...(priceMax !== "" && !Number.isNaN(Number(priceMax)) ? { price_max: Number(priceMax) } : {}),
        },
      });
      // Ignore out-of-order responses (buyer default switch fires a rapid
      // second fetch that must always win over the initial "all" fetch).
      if (mySeq !== fetchSeqRef.current) return;
      setRows(data.items);
      setTotal(data.total);
    } catch (e) {
      if (mySeq !== fetchSeqRef.current) return;
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      if (mySeq === fetchSeqRef.current) setLoading(false);
    }
  }, [page, query, dateFrom, dateTo, buyer, customer, priceMin, priceMax]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  // Scroll the freshly duplicated row into view and fade the highlight away.
  useEffect(() => {
    if (!highlightId) return;
    const el = document.querySelector(`[data-row-id="${highlightId}"]`);
    if (el) el.scrollIntoView({ block: "center", behavior: "smooth" });
    const tm = setTimeout(() => setHighlightId(null), 8000);
    return () => clearTimeout(tm);
  }, [highlightId, rows]);

  useEffect(() => {
    api.get("/procurement-quotes/salespersons").then(({ data }) => setBuyers(data)).catch(() => {});
    api.get("/procurement-quotes/quote-customers").then(({ data }) => setCustomers(data)).catch(() => {});
  }, []);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // 2026-06 · 手机端：加载更多 / 滚到底自动加载（在当前页之后继续追加）
  const mobileRows = [...rows, ...appendedRows];
  useEffect(() => { setAppendedRows([]); }, [rows]);
  const loadMoreMobile = useCallback(async () => {
    const nextPage = page + Math.floor(appendedRows.length / PAGE_SIZE) + 1;
    setLoadingMore(true);
    try {
      const { data } = await api.get("/procurement-quotes", {
        params: {
          page: nextPage,
          page_size: PAGE_SIZE,
          ...(query ? { search: query } : {}),
          ...(dateFrom ? { date_from: dateFrom } : {}),
          ...(dateTo ? { date_to: dateTo } : {}),
          ...(buyer !== "all" ? { salesperson: buyer } : {}),
          ...(customer !== "all" ? { quote_customer: customer } : {}),
          ...(priceMin !== "" && !Number.isNaN(Number(priceMin)) ? { price_min: Number(priceMin) } : {}),
          ...(priceMax !== "" && !Number.isNaN(Number(priceMax)) ? { price_max: Number(priceMax) } : {}),
        },
      });
      setAppendedRows((a) => [...a, ...(data.items || [])]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoadingMore(false);
    }
  }, [page, appendedRows.length, query, dateFrom, dateTo, buyer, customer, priceMin, priceMax]);

  const activeFilterCount = [dateFrom, dateTo, buyer !== "all" ? buyer : "",
    customer !== "all" ? customer : "", priceMin, priceMax].filter(Boolean).length;

  // ESC exits fullscreen (ignored while typing in an input so inline edits keep ESC).
  useEffect(() => {
    if (!fullscreen) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setFullscreen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fullscreen]);

  return (
    <PqFullscreenBox active={fullscreen}>
      <div className={`flex flex-wrap items-center justify-between gap-2 ${fullscreen ? "mb-1.5" : "mb-3"}`}>
        <h1 className="font-heading text-base font-bold tracking-tight flex items-center gap-2">
          <ClipboardList size={26} /> {t("pq.title")}
          <span className="font-mono text-sm font-normal text-muted-foreground ml-1" data-testid="pq-total-count">
            {total.toLocaleString()} {t("pq.records")}
          </span>
        </h1>
        <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
          <div className="relative flex-1 md:flex-none">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("pq.searchPlaceholder")}
              className="pl-8 h-11 md:h-9 w-full md:w-56 text-base md:text-sm"
              data-testid="pq-search-input"
            />
          </div>
          {/* 2026-06 · 手机端：筛选收进抽屉，字段自选 */}
          <Button variant="outline" size="sm" className="md:hidden h-11 px-3 relative"
            onClick={() => setMobileFilterOpen(true)} data-testid="pq-mobile-filter-button">
            <SlidersHorizontal size={15} />
            {activeFilterCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-brand text-white text-[10px] font-bold flex items-center justify-center"
                data-testid="pq-mobile-filter-count">{activeFilterCount}</span>
            )}
          </Button>
          <Button variant="outline" size="sm" className="md:hidden h-11 px-3"
            onClick={() => setFieldSheetOpen(true)} data-testid="pq-mobile-fields-button">
            <ListFilter size={15} />
          </Button>
          {/* 2026-06 · 手机端新增按钮（工具栏内，和右下角悬浮按钮并存） */}
          {canCreate && (
            <Button size="sm" className="md:hidden h-11 px-3 bg-brand hover:bg-brand-hover"
              onClick={() => setNewDialogOpen(true)} data-testid="pq-mobile-new-button">
              <Plus size={15} /> {t("pq.newButton")}
            </Button>
          )}
          <div className="hidden md:flex md:flex-wrap md:items-center md:gap-2">
          {/* 2026-02-09 · Date range filter restored (blank by default). */}
          <div className="flex items-center gap-1">
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
              className="h-9 w-36 font-mono text-xs"
              title={t("pq.dateFrom")}
              data-testid="pq-date-from-input"
            />
            <span className="text-muted-foreground text-xs">–</span>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
              className="h-9 w-36 font-mono text-xs"
              title={t("pq.dateTo")}
              data-testid="pq-date-to-input"
            />
          </div>
          <Select value={buyer} onValueChange={(v) => { setBuyer(v); setPage(1); }}>
            <SelectTrigger className="h-9 w-36" data-testid="pq-buyer-select">
              <SelectValue placeholder={t("pq.allBuyers")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("pq.allBuyers")}</SelectItem>
              {buyers.map((b) => (
                <SelectItem key={b} value={b}>{b}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* 2026-09 · 报价客户 filter — free text + autocomplete (user spec):
              type any customer name, existing ones are suggested. */}
          <Input
            list="pq-customer-options"
            value={customer === "all" ? "" : customer}
            placeholder={t("pq.allCustomers")}
            onChange={(e) => {
              const v = e.target.value;
              setCustomer(v.trim() ? v : "all");
              setPage(1);
            }}
            className="h-9 w-40 text-sm"
            data-testid="pq-customer-input"
          />
          <datalist id="pq-customer-options">
            {customers.map((c) => <option key={c} value={c} />)}
          </datalist>
          {/* 2026-02-09 · 采购价格区间 filter (min / max, in the quote row's currency). */}
          <div className="flex items-center gap-1">
            <Input
              type="number"
              min="0"
              step="0.01"
              value={priceMin}
              onChange={(e) => { setPriceMin(e.target.value); setPage(1); }}
              placeholder={t("pq.priceMin") || "最低价"}
              className="h-9 w-24 font-mono text-xs"
              data-testid="pq-price-min-input"
            />
            <span className="text-muted-foreground text-xs">–</span>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={priceMax}
              onChange={(e) => { setPriceMax(e.target.value); setPage(1); }}
              placeholder={t("pq.priceMax") || "最高价"}
              className="h-9 w-24 font-mono text-xs"
              data-testid="pq-price-max-input"
            />
          </div>
          {(dateFrom || dateTo || buyer !== "all" || customer !== "all" || priceMin !== "" || priceMax !== "" || search) && (
            <Button
              variant="ghost"
              size="sm"
              className="h-9 rounded-md text-xs text-muted-foreground"
              onClick={() => { setSearch(""); setDateFrom(""); setDateTo(""); setBuyer("all"); setCustomer("all"); setPriceMin(""); setPriceMax(""); setPage(1); }}
              data-testid="pq-clear-filters"
            >
              {t("pq.clearFilters")}
            </Button>
          )}
          </div>
          {/* Column widths dialog trigger (2026-02 v3) — reliable alternative to
              drag-resize for users who find the header handles finicky. */}
          <Button
            variant="outline"
            size="sm"
            className="h-9 rounded-md hidden md:inline-flex"
            onClick={() => setColDialogOpen(true)}
            data-testid="pq-column-widths-button"
            title={t("pq.columnWidths")}
          >
            <Columns size={14} /> {t("pq.columnWidths")}
          </Button>
          {/* 2026-06 · 全屏 / 退出全屏 */}
          <Button
            variant="outline"
            size="sm"
            className="h-9 rounded-md hidden md:inline-flex"
            onClick={() => setFullscreen((v) => !v)}
            title={fullscreen ? t("pq.exitFullscreen") : t("pq.fullscreen")}
            data-testid="pq-fullscreen-button"
          >
            {fullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          </Button>
          {/* 2026-02-27 · "Edit Mode" toggle removed — owned rows are now
              directly inline-editable per user spec. State + handler kept so
              downstream references stay stable during the transition. */}
          {canCreate && (
            <Button
              size="sm"
              variant="outline"
              className="h-9 rounded-md hidden md:inline-flex border-brand/40 text-brand hover:bg-brand/5"
              onClick={() => setTemplatePickerOpen(true)}
              data-testid="pq-new-from-template-button"
            >
              <Plus size={14} /> 从模版新建
            </Button>
          )}
          {canCreate && (
            <Button
              size="sm"
              className="h-9 rounded-md bg-brand hover:bg-brand-hover transition-colors hidden md:inline-flex"
              onClick={() => setNewDialogOpen(true)}
              data-testid="pq-new-button"
            >
              <Plus size={14} /> {t("pq.newButton")}
            </Button>
          )}
        </div>
      </div>

      {/* 2026-06 · 手机端悬浮新建按钮 —— 必须 portal 到 body：外层 animate-fade-in-up
          的 transform 会成为 fixed 的包含块，按钮会被钉在页面底部而不是视口右下角。 */}
      {canCreate && createPortal(
        <button type="button" onClick={() => setNewDialogOpen(true)} data-testid="pq-mobile-new-fab"
          className="md:hidden fixed right-4 bottom-20 z-40 h-14 w-14 rounded-full bg-brand text-white shadow-lg flex items-center justify-center active:scale-95 transition-transform">
          <Plus size={24} />
        </button>,
        document.body,
      )}

      <PqMobileFilterSheet
        open={mobileFilterOpen}
        onOpenChange={setMobileFilterOpen}
        value={{ dateFrom, dateTo, buyer, customer, priceMin, priceMax }}
        onChange={(v) => {
          setDateFrom(v.dateFrom); setDateTo(v.dateTo); setBuyer(v.buyer);
          setCustomer(v.customer); setPriceMin(v.priceMin); setPriceMax(v.priceMax);
          setPage(1);
        }}
        buyers={buyers}
        customers={customers}
        onClear={() => {
          setSearch(""); setDateFrom(""); setDateTo(""); setBuyer("all");
          setCustomer("all"); setPriceMin(""); setPriceMax(""); setPage(1);
          setMobileFilterOpen(false);
        }}
      />

      <NewProcurementQuoteDialog
        open={newDialogOpen}
        onOpenChange={setNewDialogOpen}
        onCreated={() => fetchRows()}
      />

      {/* 2026-09-30 · 从模版新建 —— 挑模版后字段按模版/种类默认预填，公式实时算价 */}
      <TemplatePickerDialog
        open={templatePickerOpen}
        onOpenChange={setTemplatePickerOpen}
        onPick={({ template, categoryDefaults }) => {
          setActiveTemplate(template);
          setActiveCategoryDefaults(categoryDefaults || {});
        }}
      />
      <NewProcurementQuoteDialog
        open={Boolean(activeTemplate)}
        onOpenChange={(o) => { if (!o) { setActiveTemplate(null); setActiveCategoryDefaults(null); } }}
        onCreated={(opts) => {
          if (!opts?.keepOpen) { setActiveTemplate(null); setActiveCategoryDefaults(null); }
          fetchRows();
        }}
        template={activeTemplate}
        categoryDefaults={activeCategoryDefaults}
        onClearTemplate={() => { setActiveTemplate(null); setTemplatePickerOpen(true); }}
      />

      <NewProcurementQuoteDialog
        open={Boolean(editingRow)}
        onOpenChange={(o) => { if (!o) setEditingRow(null); }}
        onCreated={(opts) => { if (!opts?.keepOpen) setEditingRow(null); fetchRows(); }}
        initialValue={editingRow}
        {...(() => {
          // 2026-06 · 上一页 / 下一页 — walk the currently loaded rows.
          if (!editingRow) return {};
          const idx = rows.findIndex((r) => r.id === editingRow.id);
          if (idx < 0) return {};
          return {
            navInfo: { index: idx, total: rows.length },
            onPrev: idx > 0 ? () => setEditingRow(rows[idx - 1]) : null,
            onNext: idx < rows.length - 1 ? () => setEditingRow(rows[idx + 1]) : null,
          };
        })()}
      />

      <Dialog open={colDialogOpen} onOpenChange={setColDialogOpen}>
        <DialogContent className="max-w-md" data-testid="pq-column-widths-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-base flex items-center gap-2">
              <Columns size={16} /> {t("pq.columnWidths")}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {t("pq.columnWidthsDesc")}
              <br />
              <span className="text-brand font-medium">{t("pq.columnWidthsHideHint")}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto -mx-1 px-1">
            <ul className="divide-y divide-border">
              {ALL_COLS.map((col) => (
                <li key={col.key} className="flex items-center gap-2 py-1.5" data-testid={`pq-cw-row-${col.key}`}>
                  <span className="flex-1 text-sm truncate">{col.label}</span>
                  <Input
                    type="text"
                    inputMode="numeric"
                    value={widthDrafts[col.key] ?? String(colWidths[col.key] ?? DEFAULT_COL_WIDTHS[col.key] ?? 100)}
                    onFocus={(e) => e.target.select()}
                    onChange={(e) => {
                      // Clean the raw string first (max 4 digits, no non-numeric).
                      const cleaned = e.target.value.replace(/[^\d]/g, "").slice(0, 4);
                      // Store the draft — this is what the input displays, so the
                      // cursor never jumps (React doesn't clobber the string
                      // mid-type). 2026-02 v5 fix.
                      setWidthDrafts((d) => ({ ...d, [col.key]: cleaned }));
                      // ALSO live-commit to colWidths so the table updates
                      // immediately as user types. Special case: 0 means HIDE
                      // the column entirely (2026-02 v8 spec). We accept 0, and
                      // any value in 30..800; values in between (1..29) wait for
                      // blur/Enter to avoid a flicker while user is still typing.
                      if (cleaned !== "") {
                        const n = parseInt(cleaned, 10);
                        if (!Number.isNaN(n) && (n === 0 || (n >= 30 && n <= 800))) {
                          commitWidths({ ...widthsRef.current, [col.key]: n });
                        }
                      }
                    }}
                    onBlur={() => {
                      // Final clamp on blur — flushes out-of-range drafts and clears
                      // the draft so the input goes back to reflecting colWidths.
                      // 0 stays as-is (means "hide"); anything 1..29 rounds up to 30.
                      const raw = widthDrafts[col.key];
                      if (raw !== undefined) {
                        const n = parseInt(raw, 10);
                        let clamped;
                        if (Number.isNaN(n)) clamped = DEFAULT_COL_WIDTHS[col.key] || 100;
                        else if (n === 0) clamped = 0;
                        else clamped = Math.max(30, Math.min(800, n));
                        commitWidths({ ...widthsRef.current, [col.key]: clamped });
                        setWidthDrafts((d) => { const { [col.key]: _, ...rest } = d; return rest; });
                      }
                    }}
                    onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                    className="w-20 h-8 font-mono text-xs text-right"
                    data-testid={`pq-cw-input-${col.key}`}
                  />
                  <span className="text-[10px] text-muted-foreground">px</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => resetColWidth(col.key)}
                    className="h-8 w-8 p-0"
                    data-testid={`pq-cw-reset-${col.key}`}
                    title={t("common.reset")}
                  >
                    <RotateCcw size={13} />
                  </Button>
                </li>
              ))}
            </ul>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={resetAllColWidths}
              className="rounded-md"
              data-testid="pq-cw-reset-all"
            >
              <RotateCcw size={13} /> {t("pq.columnWidthsResetAll")}
            </Button>
            <Button
              size="sm"
              onClick={() => setColDialogOpen(false)}
              className="rounded-md bg-brand hover:bg-brand-hover"
              data-testid="pq-cw-close"
            >
              {t("common.done")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Per-item-number modification history dialog (2026-02-26) */}
      <PqHistoryDialog
        itemNumber={historyItem}
        open={Boolean(historyItem)}
        onOpenChange={(o) => { if (!o) setHistoryItem(null); }}
      />

      {/* 2026-06 · 其他明细 straight from the list cell (no edit dialog needed) */}
      <OtherCostDetailsDialog
        open={Boolean(otherDetailsRow)}
        onOpenChange={(o) => {
          if (o || !otherDetailsRow) return;
          const original = rows.find((r) => r.id === otherDetailsRow.id) || otherDetailsRow;
          const details = otherDetailsRow.others_details || [];
          if (JSON.stringify(original.others_details || []) !== JSON.stringify(details)) {
            saveOtherDetails(original, details);
          }
          setOtherDetailsRow(null);
        }}
        rows={otherDetailsRow?.others_details || []}
        onChange={(details) => setOtherDetailsRow((r) => (r ? { ...r, others_details: details } : r))}
      />


      {/* 2026-06 · 备注小窗：整段阅读 / 粘贴长文本，有编辑权限的行可直接保存 */}
      <Dialog open={Boolean(noteRow)} onOpenChange={(o) => { if (!o) setNoteRow(null); }}>
        <DialogContent className="max-w-xl" data-testid="pq-remark-dialog">
          <DialogHeader>
            <DialogTitle>{t("pq.remark")}</DialogTitle>
            <DialogDescription className="font-mono text-xs">
              {noteRow?.item_number} · {noteRow?.name_cn}
            </DialogDescription>
          </DialogHeader>
          <textarea
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value)}
            readOnly={!noteRow || !canEditRow(noteRow)}
            rows={10}
            data-testid="pq-remark-dialog-textarea"
            className="w-full text-sm leading-6 p-2 rounded-md border border-border bg-background whitespace-pre-wrap break-words focus:outline-none focus:ring-1 focus:ring-brand read-only:bg-secondary/40"
          />
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setNoteRow(null)} data-testid="pq-remark-dialog-close">
              {t("common.close")}
            </Button>
            {noteRow && canEditRow(noteRow) && (
              <Button
                size="sm"
                onClick={async () => {
                  const target = rows.find((r) => r.id === noteRow.id) || noteRow;
                  await saveRowField(target, "remark", noteDraft);
                  setNoteRow(null);
                }}
                data-testid="pq-remark-dialog-save"
              >
                {t("common.save")}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* 2026-06 · 手机端卡片列表（采购主用：字段自选 / 拍照换图 / 加载更多） */}
      <PqMobileList
        rows={mobileRows}
        loading={loading}
        columns={COLUMNS}
        cellValue={cellValue}
        canEditRow={canEditRow}
        canDuplicate={canDuplicate}
        isSameDayOwn={isSameDayOwn}
        onEdit={(r) => setEditingRow(r)}
        onDuplicate={handleDuplicate}
        onDelete={handleDelete}
        onHistory={(n) => setHistoryItem(n)}
        onPhotoUploaded={() => fetchRows()}
        page={page}
        totalPages={totalPages}
        setPage={setPage}
        hasMore={mobileRows.length < total}
        onLoadMore={loadMoreMobile}
        loadingMore={loadingMore}
        fieldSheetOpen={fieldSheetOpen}
        setFieldSheetOpen={setFieldSheetOpen}
      />

      {/* Desktop table (visible on md+) — 2026-02 spec:
          · font size bumped one tier (text-xs → text-sm; heading uppercase 11px)
          · every <col> width driven by ``colWidths`` state, mutated via <th> resize handles
          · rows wrap naturally (no whitespace-nowrap on cells / no fixed max-w) so
            long values auto-grow the row height instead of truncating */}
      <div
        ref={tableScrollRef}
        style={!fullscreen && boxMaxH ? { maxHeight: boxMaxH } : undefined}
        className={`hidden md:block bg-card border border-border rounded-md overflow-auto ${fullscreen ? "flex-1 min-h-0" : ""}`}
      >
        <table
          className="text-sm"
          style={{ tableLayout: "fixed", width: `${totalWidth}px`, minWidth: "100%", borderCollapse: "separate", borderSpacing: 0 }}
          data-testid="pq-table"
        >
          <colgroup>
            {!isHidden("actions") && <col style={{ width: colWidths.actions }} />}
            {!isHidden("tag") && <col style={{ width: colWidths.tag }} />}
            {COLUMNS.filter((c) => !isHidden(c.key)).map((c) => (
              <col key={c.key} style={{ width: colWidths[c.key] || 100 }} />
            ))}
          </colgroup>
          <thead>
            <tr className="bg-[#d9d9d9] dark:bg-secondary">
              {!isHidden("actions") && (
              <th className="sticky top-0 left-0 z-40 bg-[#d9d9d9] dark:bg-secondary border-l border-r border-b border-t border-[#7f7f7f] pl-2 pr-4 py-1 text-center font-bold text-xs text-foreground select-none" data-testid="pq-col-actions">
                <span className="block truncate">{t("common.actions")}</span>
                <span onMouseDown={(e) => startResize(e, "actions")} onDoubleClick={() => autoFitColumn("actions")} data-testid="pq-resize-actions" className="group absolute top-0 -right-[8px] z-20 h-full w-5 cursor-col-resize flex items-center justify-center" title={t("pq.resizeHint")}>
                  <span className="block h-full w-[3px] bg-border group-hover:bg-brand group-active:bg-brand transition-colors" />
                </span>
              </th>
              )}
              {!isHidden("tag") && (
              <th className="sticky top-0 z-30 bg-[#d9d9d9] dark:bg-secondary border-r border-b border-t border-[#7f7f7f] pl-2 pr-4 py-1 text-center font-bold text-xs text-foreground select-none" data-testid="pq-col-tag">
                <span className="block truncate">{t("pq.tag")}</span>
                <span onMouseDown={(e) => startResize(e, "tag")} onDoubleClick={() => autoFitColumn("tag")} data-testid="pq-resize-tag" className="group absolute top-0 -right-[8px] z-20 h-full w-5 cursor-col-resize flex items-center justify-center" title={t("pq.resizeHint")}>
                  <span className="block h-full w-[3px] bg-border group-hover:bg-brand group-active:bg-brand transition-colors" />
                </span>
              </th>
              )}
              {COLUMNS.filter((c) => !isHidden(c.key)).map((c) => (
                <th
                  key={c.key}
                  style={c.key === "item_number" ? { left: frozenActionsW } : undefined}
                  className={`sticky top-0 bg-[#d9d9d9] dark:bg-secondary border-r border-b border-t border-[#7f7f7f] pl-2 pr-4 py-1 text-center font-bold text-xs text-foreground select-none ${c.key === "item_number" ? "z-40" : "z-30"}`}
                  data-testid={`pq-col-${c.key}`}
                >
                  <span className="block truncate">{t(c.label)}</span>
                  {/* Resize handle 2026-02 v3 — 20px hit target, straddles the column
                      border (extends 8px past right edge) so it never overlaps text.
                      Double-click auto-fits column. Dialog trigger in toolbar for
                      exact-pixel typing. */}
                  <span
                    onMouseDown={(e) => startResize(e, c.key)}
                    onDoubleClick={() => autoFitColumn(c.key)}
                    data-testid={`pq-resize-${c.key}`}
                    className="group absolute top-0 -right-[8px] z-20 h-full w-5 cursor-col-resize flex items-center justify-center"
                    title={t("pq.resizeHint")}
                  >
                    <span className="block h-full w-[3px] bg-border group-hover:bg-brand group-active:bg-brand transition-colors" />
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={COLUMNS.filter((c) => !isHidden(c.key)).length + (isHidden("actions") ? 0 : 1) + (isHidden("tag") ? 0 : 1)} className="text-center py-10 text-muted-foreground border border-border" data-testid="pq-empty-state">
                  {t("pq.empty")}
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const editable = canEditRow(r);
              const dupable = canDuplicate();
              const rowKey = r.fm_record_id || r.id;
              // 2026-02-09 · Row highlight — the whole row lights up bright
              // amber when the cursor is over any cell in it.
              const isHovered = hoverRowId === r.id;
              const isNew = highlightId === r.id;
              // 冻结列必须自带不透明背景，否则滚动时会透出下层单元格。
              const frozenBg = isNew ? "bg-emerald-100" : isHovered ? "bg-amber-100" : "bg-card";
              return (
              <tr
                key={r.id}
                data-row-id={r.id}
                className={`align-top transition-colors ${isNew ? "bg-emerald-100 ring-2 ring-inset ring-emerald-500" : isHovered ? "bg-amber-100/70" : "hover:bg-secondary/40"}`}
                data-testid={`pq-row-${rowKey}`}
                onMouseEnter={() => setHoverRowId(r.id)}
                onMouseLeave={() => setHoverRowId((id) => (id === r.id ? null : id))}
              >
                {!isHidden("actions") && (
                <td className={`sticky left-0 z-10 ${frozenBg} border-l border-r border-b border-[#7f7f7f] px-1 py-0.5 whitespace-nowrap`}>
                  <div className="flex items-center gap-0.5">
                    <button
                      type="button"
                      onClick={() => setEditingRow(r)}
                      disabled={!editable}
                      title={editable ? t("pq.edit") : t("pq.editDisabledHint")}
                      data-testid={`pq-edit-${rowKey}`}
                      className="p-1 rounded hover:bg-brand/10 text-brand disabled:text-muted-foreground disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Pencil size={13} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDuplicate(r)}
                      disabled={!dupable}
                      title={t("pq.duplicate")}
                      data-testid={`pq-duplicate-${rowKey}`}
                      className="p-1 rounded hover:bg-brand/10 text-brand disabled:text-muted-foreground disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Copy size={13} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(r)}
                      disabled={!isSameDayOwn(r)}
                      title={isSameDayOwn(r) ? t("pq.delete") : t("pq.deleteDisabledHint")}
                      data-testid={`pq-delete-${rowKey}`}
                      className="p-1 rounded hover:bg-destructive/10 text-destructive disabled:text-muted-foreground disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Trash2 size={13} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setHistoryItem(r.item_number)}
                      disabled={!r.item_number}
                      title={t("pq.history")}
                      data-testid={`pq-history-${rowKey}`}
                      className="p-1 rounded hover:bg-brand/10 text-brand disabled:text-muted-foreground disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <History size={13} />
                    </button>
                  </div>
                  <PqSyncStatus record={r} />
                </td>
                )}
                {!isHidden("tag") && (
                <td className="border-r border-b border-[#7f7f7f] px-1.5 py-0.5 break-words text-center" title={r.tag || ""}>
                  {editable ? (
                    <input
                      type="text"
                      defaultValue={r.tag || ""}
                      key={`tag-${r.id}-${r.tag || ""}`}
                      disabled={savingCell === `${r.id}:tag`}
                      onBlur={(e) => saveRowField(r, "tag", e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                      data-testid={`pq-inline-tag-${rowKey}`}
                      className="w-full h-6 px-1 text-xs text-center bg-transparent border-0 rounded-none focus:outline-none focus:bg-amber-50 focus:ring-1 focus:ring-brand"
                    />
                  ) : r.tag ? (
                    <span className="inline-block px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-900 text-[11px] font-semibold">
                      {r.tag}
                    </span>
                  ) : "-"}
                </td>
                )}
                {COLUMNS.filter((c) => !isHidden(c.key)).map((c) => {
                  // 2026-02-27 · Owned rows are ALWAYS inline-editable per user
                  // spec — the previous "Edit Mode" gate has been dropped so
                  // buyers / procurement managers can autosave directly.
                  const inlineEditable = editable && INLINE_EDITABLE.has(c.key);
                  // 2026-09 · Excel 风格：细黑框、内容居中（备注/中文名称左对齐）、编号加粗
                  const alignLeft = c.key === "remark" || c.wide;
                  const frozenCol = c.key === "item_number";
                  const frozenCls = frozenCol ? ` sticky z-10 ${frozenBg}` : "";
                  const frozenStyle = frozenCol ? { left: frozenActionsW } : undefined;
                  const cellClass = `border-r border-b border-[#7f7f7f] px-1.5 py-0.5 align-middle break-words whitespace-normal ${c.mono || c.num ? "font-mono" : ""} ${c.num ? "text-right" : alignLeft ? "text-left" : "text-center"} ${c.key === "item_number" ? "font-bold" : ""}${frozenCls}`;
                  // 2026-02-27 · Dedicated 产品图片 column sits next to 报价客户.
                  // Photo fills the cell (aspect ratio preserved via object-contain
                  // inside ProductThumb). h-px + h-full trick stretches the inner
                  // div to the row height set by neighbouring text cells.
                  if (c.key === "product_photo") {
                    return (
                      <td key={c.key} className="border-r border-b border-[#7f7f7f] p-0.5 h-px" data-testid={`pq-thumb-${rowKey}`}>
                        {/* 2026-06 · Cap the height so tall photos don't blow up
                            the row (the fallback product photos vary a lot). */}
                        <div className="w-full h-full min-h-[40px] max-h-[72px] overflow-hidden">
                          <ProductThumb
                            photoUrl={r.product_photo_url || ""}
                            patternPhotoUrl={r.product_pattern_photo_url || ""}
                            compositePhotoUrl={r.product_composite_photo_url || ""}
                            alt={r.item_number || ""}
                            size="sm"
                            side="right"
                            fallbackIconSize={20}
                            className="w-full h-full"
                          />
                        </div>
                      </td>
                    );
                  }
                  if (c.key === "remark" || c.key === "name_cn") {
                    // 2026-06 · 备注 / 中文名称：列表里固定一行高度（内容自动
                    // 折行、不横向滚动），鼠标移入该单元格才展开；备注点一下
                    // 打开小窗，方便整段阅读与粘贴长文本。
                    const v = r[c.key] === null || r[c.key] === undefined ? "" : String(r[c.key]);
                    return (
                      <td key={c.key} className="border-r border-b border-[#7f7f7f] p-0 align-top group/cell" title={v}>
                        {c.key === "remark" ? (
                          <button
                            type="button"
                            onClick={() => { setNoteDraft(r.remark == null ? "" : String(r.remark)); setNoteRow(r); }}
                            data-testid={`pq-remark-cell-${rowKey}`}
                            title={t("pq.remarkOpenHint") || "点击查看 / 编辑备注"}
                            className="block w-full text-left px-1.5 py-0.5 text-xs leading-5 max-h-5 group-hover/cell:max-h-40 overflow-hidden whitespace-pre-wrap break-words transition-[max-height] duration-150 hover:bg-brand/5 cursor-pointer"
                          >
                            {v || <span className="text-muted-foreground">-</span>}
                          </button>
                        ) : inlineEditable ? (
                          <textarea
                            defaultValue={v}
                            key={`${r.id}-${c.key}-${v}`}
                            rows={1}
                            disabled={savingCell === `${r.id}:${c.key}`}
                            onBlur={(e) => saveRowField(r, c.key, e.target.value)}
                            data-testid={`pq-inline-${c.key}-${rowKey}`}
                            className="block w-full h-6 group-hover/cell:h-28 focus:h-28 px-1.5 py-0.5 text-xs leading-5 text-left bg-transparent border-0 rounded-none resize-none overflow-hidden group-hover/cell:overflow-auto focus:overflow-auto break-words focus:outline-none focus:bg-brand/5 focus:ring-1 focus:ring-brand transition-[height] duration-150"
                          />
                        ) : (
                          <div className="px-1.5 py-0.5 text-xs leading-5 text-left max-h-5 group-hover/cell:max-h-40 overflow-hidden group-hover/cell:overflow-auto whitespace-pre-wrap break-words transition-[max-height] duration-150">
                            {v}
                          </div>
                        )}
                      </td>
                    );
                  }
                  if (inlineEditable && c.key === "others") {
                    // 2026-06 · 其他 cell — inline number PLUS a 其他明细 button.
                    const locked = (r.others_details || []).length > 0;
                    const defaultV = r.others === null || r.others === undefined ? "" : String(r.others);
                    return (
                      <td key={c.key} className={cellClass}>
                        <div className="flex items-center gap-0.5">
                          <input
                            type="number" step="0.01" min={0}
                            defaultValue={defaultV}
                            key={`${r.id}-others-${defaultV}`}
                            readOnly={locked}
                            disabled={savingCell === `${r.id}:others`}
                            onBlur={(e) => saveRowField(r, "others", e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) e.currentTarget.blur(); }}
                            data-testid={`pq-inline-others-${rowKey}`}
                            title={locked ? t("pq.othersLockedHint") : ""}
                            className={`w-full h-6 px-1 text-xs text-right font-mono border-0 rounded-none focus:outline-none focus:bg-brand/5 focus:ring-1 focus:ring-brand ${locked ? "bg-secondary/60 text-muted-foreground" : "bg-transparent"}`}
                          />
                          <button type="button" onClick={() => setOtherDetailsRow(r)}
                            title={t("pq.otherDetails")}
                            data-testid={`pq-other-details-cell-${rowKey}`}
                            className="shrink-0 p-1 rounded text-brand hover:bg-brand/10 transition-colors relative">
                            <ListPlus size={13} />
                            {locked && (
                              <span className="absolute -top-0.5 -right-0.5 h-1.5 w-1.5 rounded-full bg-brand" />
                            )}
                          </button>
                        </div>
                      </td>
                    );
                  }
                  if (inlineEditable) {
                    const isNum = NUMERIC_FIELDS.has(c.key);
                    const isDate = c.key === "quote_date";
                    const raw = r[c.key];
                    const defaultV = raw === null || raw === undefined ? "" : String(raw);
                    return (
                      <td key={c.key} className={cellClass} style={frozenStyle}>
                        <input
                          type={isDate ? "date" : isNum ? "number" : "text"}
                          step={isNum ? "0.01" : undefined}
                          min={isNum ? 0 : undefined}
                          defaultValue={defaultV}
                          key={`${r.id}-${c.key}-${defaultV}`}
                          disabled={savingCell === `${r.id}:${c.key}`}
                          onBlur={(e) => saveRowField(r, c.key, e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) e.currentTarget.blur(); }}
                          data-testid={`pq-inline-${c.key}-${rowKey}`}
                          className={`w-full h-6 px-1 text-xs bg-transparent border-0 rounded-none focus:outline-none focus:bg-brand/5 focus:ring-1 focus:ring-brand ${c.num ? "text-right font-mono" : alignLeft ? "text-left" : "text-center"} ${c.key === "item_number" ? "font-bold font-mono" : ""} ${savingCell === `${r.id}:${c.key}` ? "opacity-60" : ""}`}
                        />
                      </td>
                    );
                  }
                  return (
                    <td
                      key={c.key}
                      className={cellClass}
                      style={frozenStyle}
                      title={String(r[c.key] ?? "")}
                    >
                      {cellValue(r, c)}
                    </td>
                  );
                })}
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <FloatingHScrollbar targetRef={tableScrollRef} disabled={fullscreen} />

      <div className={`flex items-center justify-between ${fullscreen ? "mt-1.5 shrink-0" : "mt-4"}`}>
        <span className="text-xs text-muted-foreground font-mono" data-testid="pq-page-info">
          {t("pq.pageInfo").replace("{page}", page).replace("{pages}", totalPages)}
        </span>
        <div className="flex items-center gap-1.5">
          <Button variant="outline" size="sm" className="h-8 rounded-md" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="pq-prev-page">
            <ChevronLeft size={14} /> {t("pq.prev")}
          </Button>
          <Button variant="outline" size="sm" className="h-8 rounded-md" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="pq-next-page">
            {t("pq.next")} <ChevronRight size={14} />
          </Button>
        </div>
      </div>
    </PqFullscreenBox>
  );
}


// -----------------------------------------------------------------------------
// PqHistoryDialog — 2026-02-26 · Modification history for a given item_number.
// Renders both the current live records and past snapshots, sorted by change
// time (newest first). Columns per user spec: 业务员 / 报价日期 / 产品编号 /
// 产品名称 / 生产工厂 / 工厂价格 (price) / 每米价格 / 产品规格 (thickness).
// 2026-02-27 · Exported so ``ProcurementHistoryDialog`` (in ProductForm) can
// reuse the same rendering when the user clicks its per-row History button.
// -----------------------------------------------------------------------------
export function PqHistoryDialog({ itemNumber, open, onOpenChange }) {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !itemNumber) return;
    let cancelled = false;
    setLoading(true);
    setItems([]);
    api.get("/procurement-quotes/history", { params: { item_number: itemNumber } })
      .then(({ data }) => {
        if (cancelled) return;
        // 2026-06 · Newest 报价日期 first (falls back to the change timestamp).
        const key = (r) => String(r.quote_date || "") ||
          String(r.changed_at || r.updated_at || r.created_at || r.synced_at || "").slice(0, 10);
        setItems([...data].sort((a, b) => key(b).localeCompare(key(a))));
      })
      .catch((e) => { if (!cancelled) toast.error(formatApiErrorDetail(e.response?.data?.detail)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, itemNumber]);

  const fmtTs = (v) => {
    if (!v) return "-";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v).slice(0, 19).replace("T", " ");
    return d.toLocaleString();
  };
  const fmtNum = (v) => (v === null || v === undefined || v === "") ? "-"
    : Number(v).toLocaleString(undefined, { maximumFractionDigits: 4 });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[1240px] max-h-[85vh] overflow-hidden flex flex-col" data-testid="pq-history-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-base flex items-center gap-2">
            <History size={16} />{" "}
            {t("pq.historyModalTitle").replace("{item}", itemNumber || "")}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-auto border border-border rounded-md" data-testid="pq-history-scroll">
          {loading && (
            <p className="text-center py-10 text-muted-foreground text-sm">…</p>
          )}
          {!loading && items.length === 0 && (
            <p className="text-center py-10 text-muted-foreground text-sm" data-testid="pq-history-empty">
              {t("pq.historyEmptyList")}
            </p>
          )}
          {!loading && items.length > 0 && (
            <table className="w-full text-sm border-collapse" data-testid="pq-history-table">
              <thead className="bg-secondary sticky top-0">
                <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground whitespace-nowrap">
                  <th className="px-2 py-1.5 border-b border-border">{t("pq.historyKind")}</th>
                  <th className="px-2 py-1.5 border-b border-border">{t("pq.historyChangedAt")}</th>
                  <th className="px-2 py-1.5 border-b border-border">{t("pq.historyAction")}</th>
                  <th className="px-2 py-1.5 border-b border-border">{t("pq.salesperson")}</th>
                  <th className="px-2 py-1.5 border-b border-border">{t("pq.quoteCustomer")}</th>
                  <th className="px-2 py-1.5 border-b border-border">{t("pq.quoteDate")}</th>
                  <th className="px-2 py-1.5 border-b border-border">{t("pq.itemNumber")}</th>
                  <th className="px-2 py-1.5 border-b border-border">{t("pq.nameCn")}</th>
                  <th className="px-2 py-1.5 border-b border-border">{t("pq.size")}</th>
                  <th className="px-2 py-1.5 border-b border-border">{t("pq.thickness")}</th>
                  <th className="px-2 py-1.5 border-b border-border">{t("pq.factory")}</th>
                  <th className="px-2 py-1.5 border-b border-border text-right">{t("pq.price")}</th>
                  <th className="px-2 py-1.5 border-b border-border text-right">{t("pq.pricePerMeter")}</th>
                  <th className="px-2 py-1.5 border-b border-border min-w-[160px]">{t("pq.remark")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r, idx) => {
                  const isCurrent = r._kind === "current";
                  const ts = r.changed_at || r.updated_at || r.created_at || r.synced_at;
                  return (
                    <tr key={`${r._kind}-${r.id}-${idx}`} className="hover:bg-secondary/40 align-top" data-testid={`pq-history-row-${idx}`}>
                      <td className="px-2 py-1 border-b border-border whitespace-nowrap">
                        {isCurrent ? (
                          <span className="inline-block px-1.5 py-0.5 rounded-sm bg-emerald-100 text-emerald-800 text-[10px] font-semibold">
                            {t("pq.historyKindCurrent")}
                          </span>
                        ) : (
                          <span className="inline-block px-1.5 py-0.5 rounded-sm bg-slate-200 text-slate-700 text-[10px] font-semibold">
                            {t("pq.historyKindHistory")}
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-1 border-b border-border font-mono text-[11px] whitespace-nowrap">{fmtTs(ts)}</td>
                      <td className="px-2 py-1 border-b border-border text-[11px]">
                        {r.action ? t(`pq.historyAction${r.action.charAt(0).toUpperCase()}${r.action.slice(1)}`) : "-"}
                      </td>
                      <td className="px-2 py-1 border-b border-border">{r.salesperson || "-"}</td>
                      <td className="px-2 py-1 border-b border-border" data-testid={`pq-history-customer-${idx}`}>{r.quote_customer || "-"}</td>
                      <td className="px-2 py-1 border-b border-border font-mono">{r.quote_date || "-"}</td>
                      <td className="px-2 py-1 border-b border-border font-mono">{r.item_number || "-"}</td>
                      <td className="px-2 py-1 border-b border-border max-w-[180px] truncate" title={r.name_cn || ""}>{r.name_cn || "-"}</td>
                      <td className="px-2 py-1 border-b border-border font-mono whitespace-nowrap" data-testid={`pq-history-size-${idx}`}>{r.size || "-"}</td>
                      <td className="px-2 py-1 border-b border-border whitespace-nowrap" data-testid={`pq-history-thickness-${idx}`}>{r.thickness || "-"}</td>
                      <td className="px-2 py-1 border-b border-border max-w-[140px] truncate" title={r.factory || ""}>{r.factory || "-"}</td>
                      <td className="px-2 py-1 border-b border-border font-mono text-right">{fmtNum(r.price)}</td>
                      <td className="px-2 py-1 border-b border-border font-mono text-right">{fmtNum(r.price_per_meter)}</td>
                      <td className="px-2 py-1 border-b border-border text-[11px] max-w-[260px] truncate" title={r.remark || ""} data-testid={`pq-history-remark-${idx}`}>{r.remark || "-"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
